import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Shield, Zap, WifiOff, Check, Loader2, Clock, ArrowRight, Settings, Cpu } from 'lucide-react'
import { useState, useEffect } from 'react'
import autoBrand from '@/assets/auto-brand.png.asset.json'

const TaskItem = ({ title, status, processor, time }) => {
  const statusIcons = {
    completed: <Check className="w-4 h-4 text-emerald-400" />,
    processing: <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />,
    pending: <Clock className="w-4 h-4 text-gray-500" />
  }
  
  const statusColors = {
    completed: 'text-emerald-400',
    processing: 'text-cyan-400',
    pending: 'text-gray-500'
  }
  
  return (
    <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-xl border border-gray-800/50 hover:border-cyan-900/50 transition-all">
      <div className="flex items-center gap-4">
        <div className="w-8 h-8 rounded-lg bg-gray-800/50 flex items-center justify-center">
          {statusIcons[status]}
        </div>
        <div>
          <p className="text-sm font-medium text-white">{title}</p>
          <p className={`text-xs ${statusColors[status]}`}>{status}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-xs text-cyan-400 font-medium">{processor}</p>
        <p className="text-xs text-gray-500">{time}</p>
      </div>
    </div>
  )
}

export default function LandingPage() {
  const [npuLoad, setNpuLoad] = useState(37.0)
  const [cpuUsage, setCpuUsage] = useState(27.9)
  
  useEffect(() => {
    const interval = setInterval(() => {
      setNpuLoad(prev => Math.min(100, Math.max(20, prev + (Math.random() - 0.5) * 10)))
      setCpuUsage(prev => Math.min(100, Math.max(15, prev + (Math.random() - 0.5) * 8)))
    }, 2000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0f1a]/80 backdrop-blur-xl border-b border-gray-800/50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                <Settings className="w-5 h-5 text-cyan-400" />
              </div>
              <span className="text-xl font-bold tracking-wide">A.U.T.O.</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-400 hover:text-white transition-colors">Features</a>
              <a href="#demo" className="text-gray-400 hover:text-white transition-colors">Live Demo</a>
              <a href="#architecture" className="text-gray-400 hover:text-white transition-colors">Architecture</a>
            </div>
            <Link to="/app/dashboard">
              <Button className="bg-cyan-500 hover:bg-cyan-600 text-black font-semibold px-6">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-950/30 via-transparent to-transparent" />
        <div className="absolute top-1/2 right-0 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-3xl -translate-y-1/2" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[500px]">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-800/50 border border-gray-700/50 mb-8">
                <span className="text-xs font-medium tracking-widest text-cyan-400">POWERED BY ARM CORTEX-X4</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6">
                <span className="text-white">Unified Task</span>
                <br />
                <span className="text-cyan-400">Orchestration</span>
              </h1>

              <img
                src={autoBrand.url}
                alt="A.U.T.O. — ARM Unified Task Orchestrator brand mark with capability-aware, resource-adaptive, privacy-first and resilient recovery highlights"
                className="w-full max-w-lg mb-8 rounded-xl bg-white/95 p-4"
                loading="lazy"
              />

              <p className="text-lg text-gray-400 mb-10 leading-relaxed">
                The first on-device AI automation system optimized for ARM processors. 
                Execute complex workflows with zero latency and military-grade security.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/app/dashboard">
                  <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-black font-semibold px-8 h-12 gap-2">
                    Launch Demo
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/app/docs">
                  <Button size="lg" variant="outline" className="border-gray-600 text-white hover:bg-white/10 px-8 h-12">
                    View Documentation
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                <img 
                  src="/images/hero-chip-visualization.png" 
                  alt="ARM Chip Visualization" 
                  className="w-full max-w-[500px] h-auto object-contain drop-shadow-2xl"
                />
                <div className="absolute top-4 right-0 lg:-right-4 bg-gray-900/90 backdrop-blur border border-gray-700/50 rounded-xl px-5 py-3 shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Inference Speed</p>
                      <p className="text-2xl font-bold text-cyan-400">12<span className="text-sm font-normal">ms</span></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Cloud Planner Section */}
      <section id="demo" className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-950/10 to-transparent" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Live Cloud Planner</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Watch A.U.T.O. analyze, plan, and execute tasks in real-time across the ARM architecture.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <Card className="bg-gray-900/30 border-gray-800/50 backdrop-blur overflow-hidden">
              <CardContent className="p-0">
                <div className="px-6 py-4 border-b border-gray-800/50 flex items-center gap-3">
                  <Cpu className="w-5 h-5 text-cyan-400" />
                  <h3 className="text-lg font-semibold text-cyan-400">Task Orchestration</h3>
                </div>
                <div className="p-6 space-y-3 max-h-[520px] overflow-y-auto">
                  <TaskItem title="UI Element Detection" status="completed" processor="Cortex-A78" time="45ms" />
                  <TaskItem title="Intent Classification" status="completed" processor="Ethos-U85 NPU" time="8ms" />
                  <TaskItem title="Secure Enclave Handshake" status="completed" processor="TrustZone" time="3ms" />
                  <TaskItem title="On-Device OCR Pass" status="completed" processor="Mali-G720" time="27ms" />
                  <TaskItem title="Action Planning" status="processing" processor="Cortex-A78" time="12ms" />
                  <TaskItem title="Screen Capture" status="processing" processor="GPU" time="-" />
                  <TaskItem title="Gesture Synthesis" status="processing" processor="Cortex-X4" time="-" />
                  <TaskItem title="Cloud Sync" status="pending" processor="5G Modem" time="-" />
                  <TaskItem title="Policy Audit Log" status="pending" processor="TrustZone" time="-" />
                  <TaskItem title="Battery-Aware Rescheduling" status="pending" processor="Cortex-A55" time="-" />
                  <TaskItem title="Model Cache Warmup" status="pending" processor="Ethos-U85 NPU" time="-" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/30 border-gray-800/50 backdrop-blur overflow-hidden">
              <CardContent className="p-0">
                <div className="px-6 py-4 border-b border-gray-800/50 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">ARM Cortex-X4</h3>
                  <span className="text-xs text-cyan-400 bg-cyan-400/10 px-3 py-1.5 rounded-full font-medium">
                    NEON OPTIMIZED • ACTIVE
                  </span>
                </div>
                <div className="p-6 space-y-6">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-400">NPU Load</span>
                      <span className="text-cyan-400 font-medium">{npuLoad.toFixed(1)}</span>
                    </div>
                    <Progress value={npuLoad} className="h-2 bg-gray-800" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-400">CPU Usage</span>
                      <span className="text-cyan-400 font-medium">{cpuUsage.toFixed(1)}</span>
                    </div>
                    <Progress value={cpuUsage} className="h-2 bg-gray-800" />
                  </div>
                  <div className="grid grid-cols-2 gap-6 pt-4 border-t border-gray-800/50">
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Memory</p>
                      <p className="text-white text-xl font-semibold">120.0<span className="text-sm font-normal text-gray-500">MB</span></p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Power</p>
                      <p className="text-white text-xl font-semibold">10.0<span className="text-sm font-normal text-gray-500">W</span></p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Card className="bg-gray-900/30 border-gray-800/50 hover:border-cyan-900/50 transition-all group">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-cyan-500/20 transition-colors">
                  <Shield className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold mb-3">On-Device Security</h3>
                <p className="text-gray-400 leading-relaxed">Data never leaves your device. All processing happens locally on the NPU.</p>
              </CardContent>
            </Card>
            <Card className="bg-gray-900/30 border-gray-800/50 hover:border-cyan-900/50 transition-all group">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-cyan-500/20 transition-colors">
                  <Zap className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Zero Latency</h3>
                <p className="text-gray-400 leading-relaxed">Optimized for ARM NEON instructions for lightning-fast inference.</p>
              </CardContent>
            </Card>
            <Card className="bg-gray-900/30 border-gray-800/50 hover:border-cyan-900/50 transition-all group">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-cyan-500/20 transition-colors">
                  <WifiOff className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Offline Capable</h3>
                <p className="text-gray-400 leading-relaxed">Full functionality without internet connection. True edge computing.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Mobile Experience Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-cyan-950/10 via-transparent to-transparent" />
        
        <div className="container mx-auto px-6 relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">Seamless Mobile Experience</h2>
          
          <div className="flex flex-col lg:flex-row items-center justify-center gap-16">
            <div className="flex gap-6 items-end">
              <div className="relative">
                <div className="absolute inset-0 bg-cyan-500/20 rounded-3xl blur-2xl" />
                <img 
                  src="/images/app-screen-1.png" 
                  alt="App Dashboard" 
                  className="relative w-56 md:w-64 rounded-3xl shadow-2xl shadow-cyan-500/10 border border-gray-800/50" 
                />
              </div>
              <div className="relative -mb-4">
                <div className="absolute inset-0 bg-cyan-500/20 rounded-3xl blur-2xl" />
                <img 
                  src="/images/app-screen-2.png" 
                  alt="App Tasks" 
                  className="relative w-56 md:w-64 rounded-3xl shadow-2xl shadow-cyan-500/10 border border-gray-800/50" 
                />
              </div>
            </div>
            
            <div className="space-y-5 text-center lg:text-left">
              {[
                'Intuitive Dashboard Interface',
                'Real-time Resource Monitoring',
                'One-tap Task Execution',
                'Detailed Performance Analytics'
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-4 justify-center lg:justify-start">
                  <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-cyan-400" />
                  </div>
                  <span className="text-lg text-gray-300">{feature}</span>
                </div>
              ))}
              <div className="pt-6">
                <Button className="bg-cyan-500 hover:bg-cyan-600 text-black font-semibold px-8 h-12">
                  Download APK
                </Button>
              </div>
            </div>
          </div>

          {/* Live Mobile A.I. Pipeline */}
          <div className="mt-20 max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h3 className="text-2xl md:text-3xl font-bold mb-3">Live Mobile A.I. Pipeline</h3>
              <p className="text-gray-400 max-w-2xl mx-auto">
                On-device intelligence running entirely on the NPU and ARM cores — no cloud round-trip, no data leakage.
              </p>
            </div>
            <div className="grid lg:grid-cols-2 gap-8">
              <Card className="bg-gray-900/30 border-gray-800/50 backdrop-blur overflow-hidden">
                <CardContent className="p-0">
                  <div className="px-6 py-4 border-b border-gray-800/50 flex items-center gap-3">
                    <Cpu className="w-5 h-5 text-cyan-400" />
                    <h4 className="text-lg font-semibold text-cyan-400">Vision & Perception</h4>
                  </div>
                  <div className="p-6 space-y-3 max-h-[460px] overflow-y-auto">
                    <TaskItem title="Camera Scene Understanding" status="completed" processor="Cortex-A78" time="18ms" />
                    <TaskItem title="Real-time AR Object Tracking" status="completed" processor="Mali-G720" time="33ms" />
                    <TaskItem title="Semantic Segmentation" status="completed" processor="Ethos-U85 NPU" time="29ms" />
                    <TaskItem title="Face Authentication" status="completed" processor="TrustZone" time="9ms" />
                    <TaskItem title="OCR (Document Scan)" status="completed" processor="Cortex-X4" time="41ms" />
                    <TaskItem title="Barcode / QR Decode" status="completed" processor="Cortex-A55" time="4ms" />
                    <TaskItem title="Super-Resolution Upscale" status="completed" processor="Mali-G720" time="31ms" />
                    <TaskItem title="Hand Gesture Recognition" status="processing" processor="Ethos-U85 NPU" time="14ms" />
                    <TaskItem title="Depth Estimation (LiDAR)" status="processing" processor="Mali-G720" time="22ms" />
                    <TaskItem title="Live Captioning" status="processing" processor="Cortex-X4" time="21ms" />
                    <TaskItem title="Anomaly Detection (Sensor Fusion)" status="pending" processor="Cortex-A78" time="-" />
                    <TaskItem title="Lane / Object Detection" status="pending" processor="Ethos-U85 NPU" time="-" />
                    <TaskItem title="Gaze Tracking" status="pending" processor="Cortex-X4" time="-" />
                    <TaskItem title="Document Boundary Detection" status="pending" processor="Cortex-A78" time="-" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-900/30 border-gray-800/50 backdrop-blur overflow-hidden">
                <CardContent className="p-0">
                  <div className="px-6 py-4 border-b border-gray-800/50 flex items-center gap-3">
                    <Cpu className="w-5 h-5 text-cyan-400" />
                    <h4 className="text-lg font-semibold text-cyan-400">Audio & On-Device LLM</h4>
                  </div>
                  <div className="p-6 space-y-3 max-h-[460px] overflow-y-auto">
                    <TaskItem title="Wake Word Detection" status="completed" processor="Ethos-U85 NPU" time="6ms" />
                    <TaskItem title="On-Device ASR (Speech-to-Text)" status="completed" processor="Cortex-A78" time="42ms" />
                    <TaskItem title="Ambient Noise Classification" status="completed" processor="Cortex-A55" time="11ms" />
                    <TaskItem title="Noise Suppression (Calls)" status="completed" processor="Mali-G720" time="8ms" />
                    <TaskItem title="Offline TTS Synthesis" status="completed" processor="Cortex-X4" time="37ms" />
                    <TaskItem title="Speaker Verification" status="completed" processor="TrustZone" time="13ms" />
                    <TaskItem title="Music Genre Recognition" status="completed" processor="Ethos-U85 NPU" time="9ms" />
                    <TaskItem title="On-Device LLM Inference" status="processing" processor="Cortex-X4" time="128ms" />
                    <TaskItem title="Private RAG (local index)" status="processing" processor="Ethos-U85 NPU" time="64ms" />
                    <TaskItem title="Battery-Aware Model Swap" status="processing" processor="Cortex-A55" time="-" />
                    <TaskItem title="Federated Model Update" status="pending" processor="5G Modem" time="-" />
                    <TaskItem title="Policy Audit Log" status="pending" processor="TrustZone" time="-" />
                    <TaskItem title="Voice Cloning Guard" status="pending" processor="TrustZone" time="-" />
                    <TaskItem title="Language ID (live)" status="pending" processor="Ethos-U85 NPU" time="-" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Mobile A.I. — Sensors & Automation */}
            <div className="grid lg:grid-cols-2 gap-8 mt-8">
              <Card className="bg-gray-900/30 border-gray-800/50 backdrop-blur overflow-hidden">
                <CardContent className="p-0">
                  <div className="px-6 py-4 border-b border-gray-800/50 flex items-center gap-3">
                    <Cpu className="w-5 h-5 text-cyan-400" />
                    <h4 className="text-lg font-semibold text-cyan-400">Sensors & Context Intelligence</h4>
                  </div>
                  <div className="p-6 space-y-3 max-h-[460px] overflow-y-auto">
                    <TaskItem title="IMU Activity Recognition" status="completed" processor="Cortex-A55" time="7ms" />
                    <TaskItem title="GPS Geofencing Check" status="completed" processor="Cortex-A78" time="4ms" />
                    <TaskItem title="Barometric Floor Detection" status="completed" processor="Cortex-A55" time="3ms" />
                    <TaskItem title="Ambient Light Adaptation" status="completed" processor="Mali-G720" time="2ms" />
                    <TaskItem title="Proximity / Pocket Detection" status="completed" processor="Cortex-A55" time="5ms" />
                    <TaskItem title="Magnetometer Heading" status="completed" processor="Cortex-A55" time="3ms" />
                    <TaskItem title="Humidity / Temp Fusion" status="completed" processor="Cortex-A55" time="6ms" />
                    <TaskItem title="Step Count Gait Analysis" status="processing" processor="Ethos-U85 NPU" time="9ms" />
                    <TaskItem title="Fall Detection (Emergency)" status="processing" processor="Cortex-A78" time="12ms" />
                    <TaskItem title="Indoor Positioning (Wi-Fi RTT)" status="processing" processor="Cortex-A78" time="26ms" />
                    <TaskItem title="Drive Mode Detection" status="pending" processor="Ethos-U85 NPU" time="-" />
                    <TaskItem title="Sleep Stage Estimation" status="pending" processor="Cortex-A55" time="-" />
                    <TaskItem title="Crowd Density Estimate" status="pending" processor="Ethos-U85 NPU" time="-" />
                    <TaskItem title="Earthquake Tremor Detect" status="pending" processor="Cortex-A55" time="-" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-900/30 border-gray-800/50 backdrop-blur overflow-hidden">
                <CardContent className="p-0">
                  <div className="px-6 py-4 border-b border-gray-800/50 flex items-center gap-3">
                    <Cpu className="w-5 h-5 text-cyan-400" />
                    <h4 className="text-lg font-semibold text-cyan-400">Edge Automation Routines</h4>
                  </div>
                  <div className="p-6 space-y-3 max-h-[460px] overflow-y-auto">
                    <TaskItem title="Smart Reply Generation" status="completed" processor="Cortex-X4" time="19ms" />
                    <TaskItem title="Calendar Conflict Resolution" status="completed" processor="Cortex-A78" time="14ms" />
                    <TaskItem title="Notification Triage" status="completed" processor="Ethos-U85 NPU" time="10ms" />
                    <TaskItem title="Screenshot Summarization" status="completed" processor="Cortex-X4" time="53ms" />
                    <TaskItem title="On-Device Translation (offline)" status="completed" processor="Ethos-U85 NPU" time="47ms" />
                    <TaskItem title="Email Draft & Tone Match" status="completed" processor="Cortex-X4" time="61ms" />
                    <TaskItem title="Clipboard Intent Parse" status="completed" processor="Ethos-U85 NPU" time="8ms" />
                    <TaskItem title="Expense Receipt Parsing" status="processing" processor="Cortex-A78" time="38ms" />
                    <TaskItem title="Battery-Aware Deferral" status="processing" processor="Cortex-A55" time="-" />
                    <TaskItem title="Smart Home Scene Trigger" status="processing" processor="Cortex-A78" time="16ms" />
                    <TaskItem title="Phishing Link Scan" status="pending" processor="TrustZone" time="-" />
                    <TaskItem title="Weekly Behavior Digest" status="pending" processor="Cortex-X4" time="-" />
                    <TaskItem title="Commute Reroute Suggest" status="pending" processor="Cortex-A78" time="-" />
                    <TaskItem title="App Update Prefetch" status="pending" processor="Cortex-A55" time="-" />
            </div>

            {/* Mobile A.I. — Health & Connectivity */}
            <div className="grid lg:grid-cols-2 gap-8 mt-8">
              <Card className="bg-gray-900/30 border-gray-800/50 backdrop-blur overflow-hidden">
                <CardContent className="p-0">
                  <div className="px-6 py-4 border-b border-gray-800/50 flex items-center gap-3">
                    <Cpu className="w-5 h-5 text-cyan-400" />
                    <h4 className="text-lg font-semibold text-cyan-400">Health & Biometrics</h4>
                  </div>
                  <div className="p-6 space-y-3 max-h-[460px] overflow-y-auto">
                    <TaskItem title="Heart Rate (PPG) Estimation" status="completed" processor="Cortex-A55" time="9ms" />
                    <TaskItem title="SpO2 Calculation" status="completed" processor="Cortex-A55" time="14ms" />
                    <TaskItem title="ECG Rhythm Screening" status="completed" processor="Ethos-U85 NPU" time="23ms" />
                    <TaskItem title="Stress Level Inference" status="completed" processor="Cortex-A78" time="17ms" />
                    <TaskItem title="HRV Variability Trend" status="completed" processor="Cortex-A55" time="11ms" />
                    <TaskItem title="Respiration Rate" status="completed" processor="Cortex-A55" time="8ms" />
                    <TaskItem title="Skin Temp Baseline" status="completed" processor="Cortex-A55" time="6ms" />
                    <TaskItem title="Atrial Fibrillation Flag" status="processing" processor="Ethos-U85 NPU" time="19ms" />
                    <TaskItem title="Workout Auto-Detect" status="processing" processor="Cortex-A78" time="31ms" />
                    <TaskItem title="VO2 Max Estimate" status="processing" processor="Cortex-X4" time="44ms" />
                    <TaskItem title="Menstrual Cycle Forecast" status="pending" processor="Cortex-A78" time="-" />
                    <TaskItem title="Hydration Reminder Trigger" status="pending" processor="Cortex-A55" time="-" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-900/30 border-gray-800/50 backdrop-blur overflow-hidden">
                <CardContent className="p-0">
                  <div className="px-6 py-4 border-b border-gray-800/50 flex items-center gap-3">
                    <Cpu className="w-5 h-5 text-cyan-400" />
                    <h4 className="text-lg font-semibold text-cyan-400">Connectivity & On-Device Sync</h4>
                  </div>
                  <div className="p-6 space-y-3 max-h-[460px] overflow-y-auto">
                    <TaskItem title="BLE Device Handshake" status="completed" processor="Cortex-A55" time="7ms" />
                    <TaskItem title="NFC Tap Auth" status="completed" processor="TrustZone" time="5ms" />
                    <TaskItem title="Wi-Fi Roam Prediction" status="completed" processor="Cortex-A78" time="13ms" />
                    <TaskItem title="Local Mesh Sync" status="completed" processor="Cortex-A78" time="29ms" />
                    <TaskItem title="Delta Sync Compression" status="completed" processor="Cortex-X4" time="22ms" />
                    <TaskItem title="UWB Spatial Anchor" status="completed" processor="Cortex-A78" time="18ms" />
                    <TaskItem title="Cellular Load Balancing" status="completed" processor="5G Modem" time="10ms" />
                    <TaskItem title="End-to-End Key Rotation" status="processing" processor="TrustZone" time="15ms" />
                    <TaskItem title="Offline Cache Reconcile" status="processing" processor="Cortex-A78" time="36ms" />
                    <TaskItem title="Peer-to-Peer Model Share" status="processing" processor="Cortex-X4" time="71ms" />
                    <TaskItem title="Telemetry Anonymization" status="pending" processor="TrustZone" time="-" />
                    <TaskItem title="Low-Power Beacon Burst" status="pending" processor="Cortex-A55" time="-" />
                  </div>
                </CardContent>
              </Card>
            </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-800/50">
        <div className="container mx-auto px-6 text-center text-gray-500 text-sm">
          © 2026 A.U.T.O. All rights reserved. Built for ARM Architecture.
        </div>
      </footer>
    </div>
  )
}